import { FunctionComponent, useEffect } from 'react';
import { BuyingComplex, BuyingMarker } from 'types/buying';
import { Nullable } from 'types/common';

export interface BuyingMapMarkerProps {
  bottomSheetVisible: boolean;
  markerInfo: BuyingMarker;
  onClickMarker: (complexInfo: BuyingComplex) => void;
  selectedComplex: Nullable<BuyingComplex>;
  map?: naver.maps.Map;
}

const BUYING_MAP_ZOOM_LEVEL = {
  sigungu: 12,
  dong: 13,
  complex: 15,
};

const BuyingMapMarker: FunctionComponent<BuyingMapMarkerProps> = (props) => {
  const {
    map,
    markerInfo,
    onClickMarker,
    selectedComplex,
    bottomSheetVisible,
  } = props;
  if (!map) return null;
  const position = new naver.maps.LatLng(
    markerInfo.point.latitude,
    markerInfo.point.longitude,
  );

  useEffect(() => {
    const marker = new naver.maps.Marker({
      map,
      position,
    });
    setMarkerIcon(marker);
    showMarkersInBounds(marker);

    const clickListener = marker.addListener('click', () => {
      const zoom = map.getZoom();

      if (zoom >= BUYING_MAP_ZOOM_LEVEL.complex) {
        if (!markerInfo.complexId) {
          // 렌더링 지연시 터치이벤트 방지
          return;
        }
        const info: BuyingComplex = {
          complexId: markerInfo.complexId,
          count: markerInfo.count,
          bldNm: markerInfo.bldNm,
        };
        onClickMarker(info);
      } else if (zoom < BUYING_MAP_ZOOM_LEVEL.sigungu) {
        map.morph(position, BUYING_MAP_ZOOM_LEVEL.sigungu);
      } else if (zoom === BUYING_MAP_ZOOM_LEVEL.sigungu) {
        map.morph(position, BUYING_MAP_ZOOM_LEVEL.dong);
      } else if (
        zoom > BUYING_MAP_ZOOM_LEVEL.sigungu &&
        zoom < BUYING_MAP_ZOOM_LEVEL.complex
      ) {
        // 동 -> 단지 카운트, 마커 밀집을 줄이기위해서 +1
        map.morph(position, BUYING_MAP_ZOOM_LEVEL.complex + 1);
      }
    });

    return () => {
      marker.removeListener(clickListener);
      marker.setMap(null);
    };
  }, [map, markerInfo, selectedComplex]);

  const showMarkersInBounds = (marker: naver.maps.Marker) => {
    if (!map) return;

    // 현재 지도 안에 있는 마커만 렌더링
    const mapBounds = map.getBounds();
    if (mapBounds.hasPoint(marker.getPosition())) {
      marker.setMap(map);
    } else {
      marker.setMap(null);
    }
  };

  const setMarkerIcon = (marker: naver.maps.Marker) => {
    // 마커 아이콘 바꿔주는 로직
    const { clusterSize, zoom, districtNm } = markerInfo;
    const MARKER_SIZE = {
      S: 56,
      M: 64,
      L: 72,
    };

    if (zoom > 14) {
      const homeImageIcon =
        '<img src="/images/buying/buying_marker.png" style="width:45px;height:51px">';
      const imageIcon: naver.maps.HtmlIcon = {
        content: homeImageIcon,
        anchor: new naver.maps.Point(21, 40),
      };

      marker.setIcon(imageIcon);

      if (
        markerInfo.complexId === selectedComplex?.complexId &&
        bottomSheetVisible
      ) {
        marker.setAnimation(naver.maps.Animation.BOUNCE);
      }
    } else {
      let markerSize = `${MARKER_SIZE.L}px`;

      if (zoom >= BUYING_MAP_ZOOM_LEVEL.sigungu && clusterSize === 'M') {
        markerSize = `${MARKER_SIZE.M}px`;
      } else if (zoom >= BUYING_MAP_ZOOM_LEVEL.sigungu && clusterSize === 'S') {
        markerSize = `${MARKER_SIZE.S}px`;
      }

      const cluster = [
        `<div style="position: absolute; top: 0; left: 0; display:flex; flex-direction: column; align-items: center; justify-content:center; background-color:#4584FFCC; width: ${markerSize}; height: ${markerSize}; border-radius:40px; border: 1px solid #4584FF;" >`,
        `<div style="font-size:10px; color: #FFFFFF;  text-align: center;">${districtNm}</div>`,
        `</div>`,
      ].join('');

      const clusteredIcon: naver.maps.HtmlIcon = {
        content: cluster,
        size: new naver.maps.Size(45, 51),
      };
      marker.setIcon(clusteredIcon);
    }
  };

  return null;
};

export default BuyingMapMarker;
